import clsx from "clsx";

import { Button } from "@/components/ui/button/Button";
import type { CaseAgenticMissionLink } from "@/services/api/case-checking";

import { caseCheckAgentName } from "./agenticCaseCheck";
import { resolveMissionAttention, type SafeAction } from "./caseCheckMissionAttention";
import { CC_PANEL } from "./caseCheckingChrome";
import { normalizeProgressSubLabel } from "./missionStageProgress";

export function CaseCheckMissionAttentionBanner({
  missionLink,
  companyId,
  progressLabel,
  onRetryChain,
  onRetryTask,
  onSkipAdjudication,
  onAddDocuments,
  onOpenMission,
  className,
  variant = "panel",
}: {
  missionLink: CaseAgenticMissionLink | null | undefined;
  companyId?: string | null;
  progressLabel?: string | null;
  onRetryChain?: () => void;
  onRetryTask?: () => void;
  onSkipAdjudication?: () => void;
  onAddDocuments?: () => void;
  onOpenMission?: () => void;
  className?: string;
  variant?: "panel" | "inline";
}) {
  const attention = resolveMissionAttention(missionLink);
  const agentName = caseCheckAgentName(companyId ?? undefined);
  const stage = progressLabel ? normalizeProgressSubLabel(progressLabel) : null;

  if (attention.kind === "running") {
    return (
      <div
        className={clsx(variant === "panel" ? CC_PANEL : "rounded-lg px-1 py-0.5", className)}
        data-testid="agentic-case-waiting-notice"
      >
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{agentName} is reviewing this case</p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
          {stage ? (
            <>
              Current step: <span className="text-slate-600 dark:text-slate-300">{stage}</span>.{" "}
            </>
          ) : null}
          Results will appear here when the file check completes.
        </p>
        {onOpenMission ? (
          <div className="mt-3">
            <Button
              plain
              type="button"
              className="px-0 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
              onClick={onOpenMission}
            >
              Open mission
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (!attention.kind) {
    return null;
  }

  const actionButtons: Array<{ action: SafeAction; label: string; onClick?: () => void }> = [];
  if (attention.safeActions.includes("retry_task") && onRetryTask) {
    actionButtons.push({ action: "retry_task", label: "Retry", onClick: onRetryTask });
  }
  if (attention.safeActions.includes("retry_chain") && onRetryChain) {
    actionButtons.push({ action: "retry_chain", label: "Recover file check", onClick: onRetryChain });
  }
  if (attention.safeActions.includes("retry_intake") && onRetryChain) {
    actionButtons.push({ action: "retry_intake", label: "Retry file pull", onClick: onRetryChain });
  }
  if (attention.safeActions.includes("add_documents") && onAddDocuments) {
    actionButtons.push({ action: "add_documents", label: "Add documents", onClick: onAddDocuments });
  }
  if (attention.safeActions.includes("skip_adjudication") && onSkipAdjudication) {
    actionButtons.push({ action: "skip_adjudication", label: "Skip adjudication", onClick: onSkipAdjudication });
  }

  return (
    <div
      className={clsx(
        variant === "panel" ? CC_PANEL : "rounded-lg border border-amber-200/80 bg-amber-50/70 px-3 py-2 dark:border-amber-500/20 dark:bg-amber-500/10",
        className,
      )}
      data-testid="case-check-mission-attention-banner"
    >
      <p className="text-sm font-medium text-amber-900 dark:text-amber-100">{attention.title}</p>
      <p className="mt-1.5 text-sm leading-relaxed text-amber-800/90 dark:text-amber-100/80">{attention.detail}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {actionButtons.map((button) => (
          <Button key={button.action} color="slate" type="button" className="!h-8 text-xs" onClick={button.onClick}>
            {button.label}
          </Button>
        ))}
        {onOpenMission ? (
          <Button
            plain
            type="button"
            className="px-0 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
            onClick={onOpenMission}
          >
            Open mission
          </Button>
        ) : null}
      </div>
    </div>
  );
}
